import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { billingApi, opsApi } from '@/api/ops';
import type { AlertView, EnvironmentView, JobView, Overview } from '@/api/opsTypes';
import { PERMISSIONS, type AccountsResponse, type AtRiskAccount } from '@/api/types';
import { getSession, hasPermission } from '@/auth/session';
import { PageHeader, Section } from '@/components/ops/Layout';
import { Icon } from '@/components/ui/Icon';
import { formatCredits } from '@/ops/credits';
import { currentPeriod, formatMinor, minorDigits } from '@/ops/format';
import { presetRange, rangeInstants } from '@/ops/money';
import { LIVE, REPLICA } from '@/ops/live';
import { stepName } from '@/ops/stepNames';
import { useEnvironments, useSelectedEnvironment } from '@/ops/useEnvironment';
import { formatWhen } from './AccountsPage';

/** One thing somebody should deal with — worst first. */
export interface AttentionItem {
  key: string;
  severity: 'critical' | 'warning' | 'info';
  text: string;
  detail?: string;
  action: { label: string; to: string };
}

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 } as const;

/**
 * Everything the Home page knows, turned into the "Needs attention" list — a pure function so it is tested without a
 * server. Each source that could not be read is simply absent (its panel says so); nothing here invents a zero.
 */
export function attentionItems(input: {
  accounts?: AccountsResponse;
  failedJobs?: JobView[];
  /** Running jobs; only deletions waiting for an owner's decision become items. */
  runningJobs?: JobView[];
  environments?: EnvironmentView[];
  alerts?: { environment: EnvironmentView; alerts: AlertView[] }[];
  atRisk?: AtRiskAccount[];
}): AttentionItem[] {
  const items: AttentionItem[] = [];

  // A deletion waits for a PERSON — another owner's approval, or the final typed confirmation (2026-10-02).
  for (const job of input.runningJobs ?? []) {
    if (job.op !== 'delete' || (job.current_step !== 'delete.approval' && job.current_step !== 'delete.confirm')) continue;
    const approval = job.current_step === 'delete.approval';
    items.push({
      key: `removal:${job.id}`,
      severity: 'warning',
      text: approval
        ? `Deleting “${job.label}” waits for another owner's approval.`
        : `Deleting “${job.label}” is backed up and waits for the final confirmation (the tenant is frozen meanwhile).`,
      detail: `${job.environment} · asked by ${job.created_by.name} ${formatWhen(job.created_at)}`,
      action: { label: 'Open the deletion', to: `/ops/provisioning/jobs/${job.id}` },
    });
  }

  for (const job of input.failedJobs ?? []) {
    const what = job.op === 'delete' ? 'Deleting' : job.op === 'discard' ? 'Cleaning up' : 'Setting up';
    items.push({
      key: `job:${job.id}`,
      severity: 'critical',
      text: `${what} “${job.label}” stopped${job.current_step ? ` at “${stepName(job.current_step)}”` : ''}.`,
      detail: `${job.environment} · started ${formatWhen(job.created_at)}`,
      action: { label: 'Open the setup', to: `/ops/provisioning/jobs/${job.id}` },
    });
  }

  for (const { environment, alerts } of input.alerts ?? []) {
    for (const alert of alerts) {
      items.push({
        key: `alert:${environment.key}:${alert.kind}:${alert.target}`,
        severity: alert.severity === 'critical' ? 'critical' : 'warning',
        text: `${environment.label || environment.key}: ${alert.detail}`,
        detail: `since ${formatWhen(alert.since)}`,
        action: { label: 'Open the servers', to: `/ops/environments/${encodeURIComponent(environment.key)}` },
      });
    }
  }

  for (const environment of input.environments ?? []) {
    if (environment.enabled && !environment.has_internal_api_key) {
      items.push({
        key: `env-key:${environment.key}`,
        severity: 'warning',
        text: `${environment.label || environment.key} has no internal key — tenants cannot be set up or read live there.`,
        action: { label: 'Set the key', to: '/ops/environments' },
      });
    }
  }

  const subs = input.accounts?.subscriptions;
  if (subs) {
    const add = (key: string, count: number, severity: AttentionItem['severity'], text: string) => {
      if (count > 0) items.push({ key, severity, text, action: { label: 'See the accounts', to: '/accounts' } });
    };
    add('subs:past_due', subs.past_due, 'critical', `${count(subs.past_due, 'account is', 'accounts are')} past due.`);
    add('subs:suspended', subs.suspended, 'warning', `${count(subs.suspended, 'account is', 'accounts are')} suspended.`);
    add('subs:lapsed', subs.trials_lapsed, 'warning',
      `${count(subs.trials_lapsed, 'trial has', 'trials have')} ended but still show as trialing — the subscription sweep is behind.`);
    add('subs:trials', subs.trials_ending_within_7_days, 'info', `${count(subs.trials_ending_within_7_days, 'trial ends', 'trials end')} within 7 days.`);
    add('subs:seats', subs.at_seat_limit, 'info', `${count(subs.at_seat_limit, 'account is', 'accounts are')} at the seat limit.`);
    add('subs:missing', subs.missing_subscriptions, 'warning',
      `${count(subs.missing_subscriptions, 'account has', 'accounts have')} no subscription record.`);
  }

  // The at-risk queue is already ordered by revenue (the most expensive account to lose first); five is enough here,
  // the full queue is one click away.
  for (const account of (input.atRisk ?? []).slice(0, 5)) {
    items.push({
      key: `risk:${account.tenant_id}`,
      severity: account.flags.some((f) => f.severity === 'critical') ? 'critical' : 'warning',
      text: `${account.name || 'An account'} shows ${count(account.risk_level, 'warning sign', 'warning signs')}.`,
      detail: account.flags.slice(0, 3).map((f) => f.title).join(' · ') || undefined,
      action: { label: 'Open the account', to: `/accounts/${account.tenant_id}` },
    });
  }

  return items.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

/**
 * Home (2026-09-30 redesign): the business at a glance and what needs somebody today. Every figure says where it comes
 * from — the replica's saved copy (with its age) or a live read — and a source the role cannot read, or that is down,
 * is left out or labelled, never shown as zero.
 */
export function HomePage() {
  const name = getSession()?.user.display_name?.split(/\s+/)[0] ?? '';
  const canAccounts = hasPermission(PERMISSIONS.analyticsRead);
  const canHosts = hasPermission(PERMISSIONS.hostsRead);
  const canJobs = hasPermission(PERMISSIONS.tenantProvision);
  const canRisk = hasPermission(PERMISSIONS.telemetryRead);
  const canCosts = hasPermission(PERMISSIONS.costsRead);

  const accounts = useQuery({ queryKey: ['accounts', false], queryFn: () => api.accounts(false), enabled: canAccounts, ...REPLICA });
  const environments = useEnvironments();
  const envs = canHosts ? environments.data?.environments ?? [] : [];
  const failedJobs = useQuery({
    queryKey: ['ops', 'jobs', 'failed'],
    queryFn: () => opsApi.provisioning.listJobs({ status: 'failed', limit: 10 }),
    enabled: canJobs,
    ...REPLICA,
  });
  const runningJobs = useQuery({
    queryKey: ['ops', 'jobs', 'running'],
    queryFn: () => opsApi.provisioning.listJobs({ status: 'running', limit: 50 }),
    enabled: canJobs,
    ...REPLICA,
  });
  // Its own cache entry: At risk reads the full queue under ['at-risk', …] — a shared key served it these 20 rows.
  const atRisk = useQuery({ queryKey: ['at-risk', false, 20], queryFn: () => api.scores.atRisk(false, 20), enabled: canRisk, ...REPLICA });
  const overviews = useQueries({
    queries: envs.filter((e) => e.enabled).map((e) => ({
      queryKey: ['ops', 'overview', e.key, 1],
      queryFn: () => opsApi.health.overview(e.key, 1),
      ...REPLICA,
    })),
  });

  // The credits tile follows the ONE environment picker in the top bar.
  const costsEnv = useSelectedEnvironment().env;
  const period = currentPeriod();
  const costs = useQuery({
    queryKey: ['ops', 'billing', costsEnv, 'costs-summary', period],
    queryFn: () => billingApi.costsSummary(costsEnv!, period),
    enabled: canCosts && !!costsEnv,
    ...LIVE,
    retry: false,
  });

  // Collected this month: billing's money report for the current UTC month, real payments only (test ones left out).
  const month = rangeInstants(presetRange('this_month')!);
  const collected = useQuery({
    queryKey: ['ops', 'billing', costsEnv, 'money-summary', month.from, month.to, 'month', '', 'exclude'],
    queryFn: () => billingApi.moneySummary(costsEnv!, { ...month, granularity: 'month', test: 'exclude' }),
    enabled: canCosts && !!costsEnv,
    ...LIVE,
  });

  const enabledEnvs = envs.filter((e) => e.enabled);
  const items = attentionItems({
    accounts: accounts.data,
    failedJobs: failedJobs.data?.jobs,
    runningJobs: runningJobs.data?.jobs,
    environments: envs,
    alerts: overviews
      .map((q, i) => (q.data ? { environment: enabledEnvs[i]!, alerts: q.data.alerts } : null))
      .filter((x): x is { environment: EnvironmentView; alerts: AlertView[] } => x !== null),
    atRisk: atRisk.data,
  });

  // A source that could not be read is NAMED — "nothing needs you" is only said when every source answered.
  const unread = [
    canJobs && failedJobs.isError ? 'failed setups' : null,
    canJobs && runningJobs.isError ? 'deletions waiting for a decision' : null,
    canRisk && atRisk.isError ? 'the at-risk queue' : null,
    canHosts && environments.isError ? 'the environments' : null,
    canHosts && overviews.some((q) => q.isError) ? 'some server alerts' : null,
    canAccounts && accounts.isError ? 'the customer accounts' : null,
  ].filter((x): x is string => x !== null);

  const a = accounts.data;
  const syncedAt = a?.accounts.reduce<string | null>((latest, x) => (!latest || x.synced_at > latest ? x.synced_at : latest), null);

  return (
    <div className="stack">
      <PageHeader title={name ? `Hello, ${name}` : 'Home'} subtitle="Your customers, money and servers — and what needs you today." />

      {canAccounts && (
        <Section title="At a glance" meta={syncedAt ? `saved copy, synced ${formatWhen(syncedAt)}` : undefined}>
          {accounts.isError ? (
            <p className="t-body note-error">The account figures could not be read.</p>
          ) : (
            <div className="kpi-row">
              <Tile label="Customers" value={a ? a.customer_accounts : null}
                sub={a ? `${a.new_accounts} new this week · ${a.internal_accounts} internal not counted` : undefined} to="/accounts" />
              <Tile label="People" value={a ? a.total_users : null}
                sub={a?.has_count_errors ? 'some accounts could not be counted' : 'users in customer accounts'} />
              <Tile label="Active this week" value={a ? a.active_accounts_7d : null}
                sub={a ? `of ${a.customer_accounts} customers` : undefined} />
              {hasPermission(PERMISSIONS.subscriptionRead) && (
                <Tile
                  label="Plan revenue"
                  value={a ? wholeMoney(a.subscriptions.mrr_minor, a.subscriptions.mrr_currency) : null}
                  sub={a?.subscriptions.mrr_is_mixed_currency
                    ? `per month · ${a.subscriptions.mrr_currency} plans only (other currencies not added)`
                    : 'per month, from the plans on record'}
                />
              )}
              {canCosts && (
                <Tile
                  label="Credits used this month"
                  value={costs.data ? formatCredits(costs.data.totals.credits, costs.data.credit_display_scale, { unit: false }) : null}
                  sub={costs.isError ? `billing on ${costsEnv} is unavailable` : costsEnv ? `${period} · live from billing on ${costsEnv}` : 'pick an environment'}
                  to="/ops/costs"
                />
              )}
              {canCosts && (
                <Tile
                  label="Collected this month"
                  // The error is checked first: a failed live read never shows the last answer (CP2).
                  value={collected.isError ? null : collected.data ? wholeMoney(collected.data.totals.gross_collected_minor, collected.data.currency) : null}
                  sub={collected.isError ? `billing on ${costsEnv} is unavailable` : costsEnv ? `${period} · real payments · live from billing on ${costsEnv}` : 'pick an environment'}
                  to="/ops/money"
                />
              )}
            </div>
          )}
        </Section>
      )}

      <Section title="Needs attention" meta={items.length ? `${items.length} item${items.length === 1 ? '' : 's'}` : undefined}>
        {unread.length > 0 && (
          <p className="t-body note-warning" style={{ margin: '0 0 8px' }} data-testid="attention-unread">
            Could not read {unread.join(', ')} — this list may be incomplete. Try again in a moment.
          </p>
        )}
        {items.length === 0 ? (
          unread.length > 0 ? null : (
            <p className="t-body muted" style={{ margin: 0 }}>
              Nothing needs you right now.{' '}
              {!canAccounts && !canHosts ? 'Your role sees little of the console — ask an owner if something is missing.' : ''}
            </p>
          )
        ) : (
          <ul className="attention-list">
            {items.map((item) => (
              <li key={item.key} className={`attention-item attention-${item.severity}`}>
                <Icon name="alert" className="attention-icon" />
                <div className="attention-text">
                  <span className="t-body">{item.text}</span>
                  {item.detail && <span className="t-caption muted">{item.detail}</span>}
                </div>
                <Link className="btn btn-ghost btn-sm" to={item.action.to}>{item.action.label}</Link>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {canHosts && enabledEnvs.length > 0 && (
        <Section title="Servers" meta="the last hour, from the collectors">
          <div className="kpi-row">
            {enabledEnvs.map((env, i) => (
              <ServerCard key={env.key} env={env} overview={overviews[i]?.data} failed={overviews[i]?.isError ?? false} />
            ))}
          </div>
        </Section>
      )}

      {canJobs && (
        <div className="row">
          <Link className="btn btn-primary" to="/ops/provisioning/new"><Icon name="plus" /> Create a tenant</Link>
          <Link className="btn btn-ghost" to="/ops/tenants">All tenants</Link>
        </div>
      )}
    </div>
  );
}

/** A headline amount in whole units (a monthly total needs no piasters); the exact figure is on the Accounts page. */
function wholeMoney(minor: number, currency: string | null): string {
  const digits = minorDigits(currency);
  return formatMinor(Math.round(minor / 10 ** digits) * 10 ** digits, currency).replace(/\.0+(?=\D*$)/, '');
}

function Tile({ label, value, sub, to }: { label: string; value: ReactNode | null; sub?: string; to?: string }) {
  const text = value === null ? '—' : typeof value === 'number' ? value.toLocaleString('en-US') : value;
  const body = (
    <>
      <div className="stat-label t-label">{label}</div>
      <div className={`stat-value tabular${typeof text === 'string' && text.length > 9 ? ' is-long' : ''}`}>{text}</div>
      {sub && <div className="stat-sub t-caption">{sub}</div>}
    </>
  );
  return to ? <Link to={to} className="card stat-tile tile-link">{body}</Link> : <div className="card stat-tile">{body}</div>;
}

function ServerCard({ env, overview, failed }: { env: EnvironmentView; overview?: Overview; failed: boolean }) {
  const hosts = overview?.hosts ?? [];
  const stale = hosts.filter((h) => h.stale).length;
  // The fullest disk is the one that takes an environment down (2026-09-27 outage) — show the least free space.
  const leastFree = hosts
    .flatMap((h) => h.disks ?? [])
    .reduce<number | null>((least, d) => (d.free_percent === null ? least : Math.min(least ?? 100, d.free_percent)), null);
  const critical = (overview?.alerts ?? []).filter((x) => x.severity === 'critical').length;

  return (
    <Link to={`/ops/environments/${encodeURIComponent(env.key)}`} className="card stat-tile tile-link">
      <div className="stat-label t-label">{env.label || env.key}</div>
      {failed ? (
        <div className="t-body note-error">Could not read the collectors.</div>
      ) : !overview ? (
        <div className="t-body muted">Loading…</div>
      ) : (
        <>
          <div className="stat-value tabular">{critical > 0 ? `${critical} alert${critical === 1 ? '' : 's'}` : 'OK'}</div>
          <div className="stat-sub t-caption">
            {hosts.length} host{hosts.length === 1 ? '' : 's'}
            {stale > 0 ? ` · ${stale} not reporting` : ''}
            {leastFree !== null ? ` · least free disk ${Math.round(leastFree)}%` : ''}
          </div>
        </>
      )}
    </Link>
  );
}
